import type { EvidenceItem } from '../types/investigator';
import { getEvidenceCategoryLabel } from './evidenceDisplay';
import { titleCasePlaceName } from './titleCasePlaceName';

/** Data quality scorecards are hidden from the Timeline panel for now (still in Evidence). */
export function isTimelineVisibleEvent(item: EvidenceItem): boolean {
  return getEvidenceCategoryLabel(item) !== 'Data quality';
}

export function filterTimelineEvidence(evidence: EvidenceItem[]): EvidenceItem[] {
  return evidence.filter(isTimelineVisibleEvent);
}

/** Pick-up / delivery lines embed stop + city in the explanation — no separate route row. */
export function isDetailedStopEvent(item: EvidenceItem): boolean {
  return /^(Picked up|Delivered) - /i.test((item.explanation || '').trim());
}

/** Pick-up → delivery line for timeline cards (not shown on detailed stop events). */
export function formatTimelineRoute(item: EvidenceItem): string | null {
  const origin = item.origin_city?.trim();
  const destination = item.destination_city?.trim();
  if (origin && destination) {
    return `${titleCasePlaceName(origin)} → ${titleCasePlaceName(destination)}`;
  }
  if (origin) return `Pick up: ${titleCasePlaceName(origin)}`;
  if (destination) return `Deliver to: ${titleCasePlaceName(destination)}`;
  return null;
}

/** First origin → destination (or partial route) across timeline-visible evidence. */
export function resolveTimelineRouteLabel(evidence: EvidenceItem[]): string | null {
  for (const item of filterTimelineEvidence(evidence)) {
    const route = formatTimelineRoute(item);
    if (route) return route;
  }
  return null;
}

/** Group key: same calendar minute in local display timezone. */
export function timelineMinuteKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const mo = d.getMonth();
  const day = d.getDate();
  const h = d.getHours();
  const min = d.getMinutes();
  return `${y}-${mo}-${day}-${h}-${min}`;
}

export interface TimelineMinuteGroup {
  minuteKey: string;
  occurred_at: string;
  items: EvidenceItem[];
}

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function formatDayPart(days: number): string {
  return days === 1 ? '+ 1 day' : `+ ${days} days`;
}

function formatHourPart(hours: number): string {
  return hours === 1 ? '1 hr' : `${hours} hrs`;
}

/**
 * Label for gaps strictly greater than 24 hours between consecutive timeline groups.
 * e.g. "+ 1 day 12 hrs", "+ 2 days 5 hrs"
 */
export function formatTimelineGapLabel(
  previousOccurredAt: string,
  nextOccurredAt: string,
): string | null {
  const prevMs = Date.parse(previousOccurredAt);
  const nextMs = Date.parse(nextOccurredAt);
  if (Number.isNaN(prevMs) || Number.isNaN(nextMs)) return null;
  const diff = nextMs - prevMs;
  if (diff <= MS_PER_DAY) return null;
  const days = Math.floor(diff / MS_PER_DAY);
  const hours = Math.floor((diff - days * MS_PER_DAY) / MS_PER_HOUR);
  const dayPart = formatDayPart(days);
  if (hours > 0) {
    return `${dayPart} ${formatHourPart(hours)}`;
  }
  return dayPart;
}

export type TimelineListEntry =
  | { kind: 'gap'; id: string; label: string }
  | { kind: 'group'; group: TimelineMinuteGroup };

/** Inserts gap rows between minute-groups when the elapsed time exceeds 24 hours. */
export function buildTimelineListEntries(groups: TimelineMinuteGroup[]): TimelineListEntry[] {
  const entries: TimelineListEntry[] = [];
  for (let i = 0; i < groups.length; i++) {
    if (i > 0) {
      const label = formatTimelineGapLabel(
        groups[i - 1].occurred_at,
        groups[i].occurred_at,
      );
      if (label) {
        entries.push({
          kind: 'gap',
          id: `gap-${groups[i - 1].minuteKey}-${groups[i].minuteKey}`,
          label,
        });
      }
    }
    entries.push({ kind: 'group', group: groups[i] });
  }
  return entries;
}

export function groupEvidenceByMinute(evidence: EvidenceItem[]): TimelineMinuteGroup[] {
  const sorted = [...evidence].sort(
    (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
  );
  const groups: TimelineMinuteGroup[] = [];
  for (const item of sorted) {
    const key = timelineMinuteKey(item.occurred_at);
    const last = groups[groups.length - 1];
    if (last && last.minuteKey === key) {
      last.items.push(item);
    } else {
      groups.push({ minuteKey: key, occurred_at: item.occurred_at, items: [item] });
    }
  }
  return groups;
}
